/**
 * BookingsKpiRow — the four counts above the fill bar (BON-490). Bookings /
 * Reservations / In checkout key the three lanes on {@link SegmentedFillBar};
 * hovering (or, for the two clickable seat cells, focusing) one reports its
 * tone (the card wires that into the bar's `highlightTone`), and clicking
 * Bookings/Reservations selects the matching tab. Tree replacements take no
 * seat, so it sits fenced past a divider, is never part of the
 * hover-highlight, and only ever affects tab selection — rendered only when
 * there's at least one.
 *
 * The row stays inset within the card's padding on every breakpoint (a flex
 * row on desktop/tablet, a 2-up grid on mobile).
 */
import type { CSSProperties } from 'react';

import type { FillSegment } from '../../primitives';

import type { TabId } from './bookingsCardTypes';
import { BookingsKpiCell } from './BookingsKpiCell';

export interface BookingsKpiRowProps {
  bookedSeats: number;
  reservedSeats: number;
  inCheckoutSeats: number;
  treeReplacementCount: number;
  activeTab: TabId;
  highlightTone: FillSegment['tone'] | null;
  onSelectTab: (tab: TabId) => void;
  onHoverTone: (tone: FillSegment['tone'] | null) => void;
  isMobile: boolean;
}

/** Mobile: an inset bordered 2-up grid. Desktop/tablet: a plain inset flex row.
 *  Both keep the card's own padding on either side (an earlier edge-bleed left
 *  the first cell with no visible left padding, since a cell's inner padding is
 *  narrower than `--nb-pad-card`). Exported so the layout is unit-testable
 *  without depending on the DOM. */
export function containerStyle(isMobile: boolean): CSSProperties {
  return isMobile
    ? {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
        background: 'var(--nb-border)',
        border: '1px solid var(--nb-border)',
        borderRadius: 10,
        overflow: 'hidden',
      }
    : {
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        gap: 2,
      };
}

export function BookingsKpiRow({
  bookedSeats,
  reservedSeats,
  inCheckoutSeats,
  treeReplacementCount,
  activeTab,
  highlightTone,
  onSelectTab,
  onHoverTone,
  isMobile,
}: BookingsKpiRowProps) {
  const hasTreeReplacements = treeReplacementCount > 0;
  const dim = (tone: FillSegment['tone']) => highlightTone !== null && highlightTone !== tone;

  return (
    <div style={containerStyle(isMobile)}>
      <BookingsKpiCell
        label="Bookings"
        value={bookedSeats}
        sub="complete"
        tone="booked"
        active={activeTab === 'bookings'}
        dim={dim('booked')}
        isMobile={isMobile}
        onClick={() => onSelectTab('bookings')}
        onHover={() => onHoverTone('booked')}
        onLeave={() => onHoverTone(null)}
        onFocus={() => onHoverTone('booked')}
        onBlur={() => onHoverTone(null)}
      />
      <BookingsKpiCell
        label="Reservations"
        value={reservedSeats}
        sub="staff-created"
        tone="reserved"
        active={activeTab === 'reservations'}
        dim={dim('reserved')}
        isMobile={isMobile}
        onClick={() => onSelectTab('reservations')}
        onHover={() => onHoverTone('reserved')}
        onLeave={() => onHoverTone(null)}
        onFocus={() => onHoverTone('reserved')}
        onBlur={() => onHoverTone(null)}
      />
      <BookingsKpiCell
        label="In checkout"
        value={inCheckoutSeats}
        sub="holds, expiring"
        tone="in-checkout"
        dim={dim('in-checkout')}
        fullRow={isMobile && !hasTreeReplacements}
        isMobile={isMobile}
        onHover={() => onHoverTone('in-checkout')}
        onLeave={() => onHoverTone(null)}
      />
      {hasTreeReplacements && (
        <>
          {!isMobile && (
            <span style={{ width: 1, background: 'var(--nb-border)', margin: '6px 0 6px 8px' }} />
          )}
          <BookingsKpiCell
            label="Tree replacements"
            value={treeReplacementCount}
            sub="no seat used"
            fenced={!isMobile}
            filled
            active={activeTab === 'tree-replacements'}
            dim={highlightTone !== null}
            isMobile={isMobile}
            onClick={() => onSelectTab('tree-replacements')}
          />
        </>
      )}
    </div>
  );
}
